import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Button
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  RadioButtonUnchecked as RadioButtonUncheckedIcon
} from '@mui/icons-material';
import { getRequest } from '../../Fetch/request';
import DSA_TOPICS from '../../Utils/DSATopics';
import { useAuth } from '../../hooks/useAuth';
import SEOHead from '../SEO/SEOHead';
import RelatedLinks from '../SEO/RelatedLinks';

const DSAQuestions = () => {
  const { isAuthenticated } = useAuth();
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedTopic, setSelectedTopic] = useState('all');
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [totalProblems, setTotalProblems] = useState(0);

  // Fetch questions on component mount
  useEffect(() => {
    fetchQuestions();
  }, []);

  // Fetch questions when topic or page changes
  useEffect(() => {
    if (selectedTopic !== 'all') {
      fetchQuestionsForTopic(selectedTopic);
    } else {
      fetchQuestions();
    }
  }, [selectedTopic, page]);

  const fetchQuestions = async () => {
    try {
      setLoading(true);
      setError(null);

      const baseURL = process.env.REACT_APP_URL;
      if (!baseURL) {
        throw new Error('Backend URL not configured');
      }

      const profile = JSON.parse(localStorage.getItem("profileObject"));
      const userId = profile?.id || localStorage.getItem('userId') || 'slazysloth_user';
      
      // Note: We now allow non-authenticated users to view DSA problems

      const questionsUrl = `${baseURL}/problems?userId=${userId}&page=${page + 1}&limit=20`;
      const questionsResponse = await getRequest(questionsUrl);
      
      if (questionsResponse.status === "200" && questionsResponse.response) {
        const fetchedQuestions = questionsResponse.response.problems || [];
        const meta = questionsResponse.response.meta || {};
        
        const transformedQuestions = fetchedQuestions.map(problem => ({
          id: problem.problemId || problem.id,
          title: problem.title || problem.problemTitle,
          rating: problem.problemRating || 1200,
          status: problem.status === "Accepted" ? "solved" : "unsolved"
        }));
        
        setQuestions(transformedQuestions);
        setTotalPages(meta.totalPages || 1);
        setTotalProblems(meta.totalProblems || fetchedQuestions.length);
      } else {
        throw new Error('Failed to fetch questions from API');
      }

    } catch (err) {
      console.error('Error fetching DSA data:', err);
      setError(err.message || 'Failed to load DSA questions');
      // Sample data matching your image
      setQuestions([
        { id: 1, title: "Warehouse Stacking", rating: 1300, status: "unsolved" },
        { id: 2, title: "Fence Repair", rating: 1100, status: "unsolved" },
        { id: 3, title: "Traffic Speed Monitoring", rating: 1800, status: "unsolved" },
        { id: 4, title: "Neighborhood Rent Adjustment", rating: 1500, status: "unsolved" },
        { id: 5, title: "Team Formation", rating: 1100, status: "unsolved" },
        { id: 6, title: "Machine Gear Compatibility", rating: 1300, status: "unsolved" },
        { id: 7, title: "Festival Sponsorship", rating: 1800, status: "unsolved" },
        { id: 8, title: "Job Fair Matching", rating: 1100, status: "unsolved" },
        { id: 9, title: "Workload Balancing", rating: 1400, status: "unsolved" }
      ]);
    } finally {
      setLoading(false);
    }
  };

  const fetchQuestionsForTopic = async (topicKey) => {
    try {
      setLoading(true);
      setError(null);

      const baseURL = process.env.REACT_APP_URL;
      if (!baseURL) {
        throw new Error('Backend URL not configured');
      }

      const profile = JSON.parse(localStorage.getItem("profileObject"));
      const userId = profile?.id || localStorage.getItem('userId') || 'slazysloth_user';
      
      // Note: We now allow non-authenticated users to view DSA problems

      // Get the backend topic value from DSA_TOPICS mapping
      const topicValue = DSA_TOPICS[topicKey];
      const questionsUrl = `${baseURL}/problems?userId=${userId}&page=${page + 1}&limit=20&topicId=${encodeURIComponent(topicValue)}`;
      const questionsResponse = await getRequest(questionsUrl);
      
      if (questionsResponse.status === "200" && questionsResponse.response) {
        const fetchedQuestions = questionsResponse.response.problems || [];
        const meta = questionsResponse.response.meta || {};
        
        const transformedQuestions = fetchedQuestions.map(problem => ({
          id: problem.problemId || problem.id,
          title: problem.title || problem.problemTitle,
          rating: problem.problemRating || 1200,
          status: problem.status === "Accepted" ? "solved" : "unsolved"
        }));
        
        setQuestions(transformedQuestions);
        setTotalPages(meta.totalPages || 1);
        setTotalProblems(meta.totalProblems || fetchedQuestions.length);
      } else {
        // If no questions found for this topic, show empty list
        setQuestions([]);
        setTotalPages(1);
        setTotalProblems(0);
      }

    } catch (err) {
      console.error('Error fetching questions for topic:', err);
      setError(`Failed to load questions for ${topicKey}`);
      setQuestions([]);
    } finally {
      setLoading(false);
    }
  };

  const getRatingColor = (rating) => {
    if (rating >= 1600) return '#F44336'; // Red for very hard (1600+)
    if (rating >= 1500) return '#FF9800'; // Orange for hard (1500-1599)
    return '#4CAF50'; // Green for medium and easy (below 1500)
  };

  const handleQuestionClick = (questionId) => {
    window.location.href = `/problem-statement?problemId=${questionId}`;
  };

  const handleTopicChange = (event) => {
    setSelectedTopic(event.target.value);
    setPage(0); // Reset to first page when changing topics
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleDSASession = () => {
    if (!isAuthenticated) {
      alert('Please login to access DSA sessions');
      return;
    }
    window.location.href = '/session-details';
  };

  // Generate topic options from DSA_TOPICS
  const topicOptions = Object.keys(DSA_TOPICS).map(key => ({
    value: key,
    label: key.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, l => l.toUpperCase())
  }));

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress size={60} />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ marginTop: '20px' }}>
      <SEOHead 
        title="DSA Practice Questions - Data Structures & Algorithms | SlazySloth"
        description="Practice Data Structures and Algorithms questions with AI feedback. Master arrays, linked lists, trees, graphs, dynamic programming, and more. Perfect for coding interview preparation."
        keywords="DSA practice, data structures, algorithms, coding questions, programming interview, leetcode practice, array problems, tree problems, graph algorithms, dynamic programming"
        url="https://slazysloth.com/dsa"
        structuredData={{
          "@context": "https://schema.org",
          "@type": "Course",
          "name": "DSA Practice Questions",
          "description": "Comprehensive Data Structures and Algorithms practice with AI feedback",
          "provider": {
            "@type": "Organization",
            "name": "SlazySloth"
          },
          "educationalLevel": "Intermediate to Advanced",
          "teaches": ["Data Structures", "Algorithms", "Problem Solving", "Coding Interview Preparation"]
        }}
      />
      
      {/* Header */}
      <Box sx={{ mb: 4, textAlign: 'center' }}>
        <Typography component="h1" variant="h3" sx={{ fontWeight: 'bold', mb: 2, color: '#333' }}>
          DSA Practice Questions
        </Typography>
        <Typography variant="h6" sx={{ color: '#666', maxWidth: 800, mx: 'auto' }}>
          Master Data Structures and Algorithms with our comprehensive question bank. 
          Practice coding problems, get AI feedback, and prepare for technical interviews.
        </Typography>
      </Box>

      {/* Topic Selection and DSA Session Button */}
      <Box sx={{ marginBottom: '20px' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <FormControl sx={{ minWidth: 200 }}>
            <InputLabel id="topic-select-label">Filter by Topic</InputLabel>
            <Select
              labelId="topic-select-label"
              id="topic-select"
              value={selectedTopic}
              label="Filter by Topic"
              onChange={handleTopicChange}
              size="small"
            >
              <MenuItem value="all">All Topics</MenuItem>
              {topicOptions.map((topic) => (
                <MenuItem key={topic.value} value={topic.value}>
                  {topic.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          
          {/* DSA Session Button */}
          <Button
            variant="contained"
            onClick={handleDSASession}
            sx={{
              backgroundColor: '#008080',
              '&:hover': { backgroundColor: '#006666' },
              borderRadius: '8px',
              textTransform: 'none',
              fontWeight: 'bold',
              px: 3,
              py: 1
            }}
          >
            DSA Session
          </Button>
        </Box>
      </Box>

      <Paper elevation={0} sx={{ borderRadius: '8px', overflow: 'hidden', backgroundColor: 'transparent' }}>
        <TableContainer>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell align="center" sx={{ fontWeight: 'bold', fontSize:'14px', py:0.5, backgroundColor: 'transparent' }}>
                  Status
                </TableCell>
                <TableCell sx={{ fontWeight: 'bold', fontSize:'14px', py:0.5, backgroundColor: 'transparent' }}>
                  Title
                </TableCell>
                <TableCell align="center" sx={{ fontWeight: 'bold', fontSize:'14px', py:0.5, backgroundColor: 'transparent' }}>
                  Rating
                </TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {questions.map((question) => (
                  <TableRow key={question.id} hover>
                    {/* Status */}
                    <TableCell align="center" sx={{ py: 0.5 }}>
                      {question.status === 'solved' ? (
                        <CheckCircleIcon sx={{ color: '#4CAF50', fontSize: '1.2rem' }} />
                      ) : (
                        <RadioButtonUncheckedIcon sx={{ color: '#666', fontSize: '1.2rem' }} />
                      )}
                    </TableCell>

                    {/* Title (Clickable Text) */}
                    <TableCell
                      sx={{
                        cursor: 'pointer',
                        color: 'primary.main',
                        textDecoration: 'none',
                        fontSize: '0.9rem',
                        fontWeight: 600,
                        letterSpacing: '0.01em',
                        py: 0.5,
                      }}
                    >
                      <a
                        href={`/problem-statement?problemId=${question.id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ color: 'inherit', textDecoration: 'none', fontWeight: 600, fontSize: '0.9rem', letterSpacing: '0.01em' }}
                      >
                        {question.title}
                      </a>
                    </TableCell>

                    {/* Problem Rating */}
                    <TableCell align="center" sx={{ py: 0.5 }}>
                      <Typography variant="caption" sx={{ 
                        fontSize: '0.8rem',
                        fontWeight: 'bold',
                        color: getRatingColor(question.rating)
                      }}>
                        {question.rating}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </TableContainer>
        
        {/* Bottom navigation arrows */}
        <Box sx={{ display:'flex', justifyContent:'flex-end', alignItems:'center', p:0.5, gap:0.5 }}>
          <IconButton 
            aria-label="previous page" 
            size="small" 
            disabled={page === 0} 
            onClick={(e) => handleChangePage(e, page - 1)}
          >
            ‹
          </IconButton>
          <Typography variant="caption" sx={{ minWidth:50, textAlign:'center', fontSize: '0.8rem' }}>
            {`${page + 1} / ${totalPages}`}
          </Typography>
          <IconButton 
            aria-label="next page" 
            size="small" 
            disabled={page >= totalPages - 1} 
            onClick={(e) => handleChangePage(e, page + 1)}
          >
            ›
          </IconButton>
        </Box>
      </Paper>

      {error && (
        <Alert severity="info" sx={{ mt: 2 }}>
          {error}
        </Alert>
      )}
      
      {/* Related Links for SEO and User Experience */}
      <RelatedLinks currentPage="dsa" />
    </Container>
  );
};

export default DSAQuestions;
